import * as React from "react";
import { Box, CPHrBreak, CPText } from "shared/components/base";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPActionsheetModal from "shared/components/shared/CPActionsheetModal/CPActionsheetModal";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";
import CPEmailTextBox from "shared/components/shared/CPEmailTextBox/CPEmailTextBox";
import CPCheckbox from "shared/components/shared/CPCheckbox/CPCheckbox";
import CPListBox, {
  CPBoxOption,
} from "shared/components/shared/CPListBox/CPListBox";
import CPDropDownList from "shared/components/shared/CPDropDownList/CPDropDownList";
import CPModal from "shared/components/shared/CPModal/CPModal";
import CPHelpButton from "shared/components/shared/CPHelpButton/CPHelpButton";
import {
  useAgenciesLookupQuery,
  useRolesLookupQuery,
} from "data/lookup/lookupQueries";
import { User, UserUpdateRequest, useUpsertUserMutation, useAdminUserDetailQuery } from "data/user/user";
import useAppState from "shared/state/useAppState";

const SIGNON_OPTIONS = [
  { text: "Microsoft SSO", value: "AZURE_SSO" },
  { text: "Passwordless Signon", value: "LOCAL_IDENTITY" },
  { text: "Shared Test Account", value: "TEST_ACCOUNT" },
];

interface SignonHelpModalProps {
  authType: string | undefined;
  open: boolean;
  onClose: () => void;
}

const BADGE_STYLE: React.CSSProperties = {
  background: "#e3f2fd",
  color: "#0066cc",
  border: "1px solid #90caf9",
  borderRadius: 3,
  padding: "1px 7px",
  fontSize: 11,
  whiteSpace: "nowrap" as const,
};

const TD_LABEL: React.CSSProperties = {
  fontWeight: 600,
  fontSize: 13,
  paddingBottom: 6,
  paddingRight: 16,
  verticalAlign: "top",
  whiteSpace: "nowrap" as const,
  width: 150,
};

const TD_VALUE: React.CSSProperties = {
  fontSize: 13,
  paddingBottom: 6,
  verticalAlign: "top",
  color: "#444",
};

const rows: Array<{
  value: string;
  label: string;
  desc: string;
  notes: string[];
}> = [
  {
    value: "AZURE_SSO",
    label: "Microsoft SSO",
    desc: "For employees/agents with a company Microsoft account. Identity is managed entirely by Microsoft.",
    notes: [
      "User must exist in Azure Active Directory.",
      "Must be assigned to the appropriate Azure security groups.",
    ],
  },
  {
    value: "LOCAL_IDENTITY",
    label: "Passwordless",
    desc: "For external users without a Microsoft account. A one-time code is emailed each sign-in.",
    notes: [
      "User enters their email on the login page.",
      "A short-lived code is sent and must be used promptly.",
    ],
  },
  {
    value: "TEST_ACCOUNT",
    label: "Shared Test Account",
    desc: "For internal QA only. Password-based, shareable account. Email must end in @test.local.",
    notes: [
      "Email must end in @test.local (e.g. driver.test@test.local).",
      "Password is set by an admin and shared with the team.",
      "Do not use for real users or production data.",
    ],
  },
];

const SignonHelpModal: React.FC<SignonHelpModalProps> = ({
  authType,
  open,
  onClose,
}) => (
  <CPModal title="Sign On Options" open={open} onClose={onClose} width={560}>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <tbody>
        {rows.map((row, i) => (
          <React.Fragment key={row.value}>
            <tr>
              <td style={TD_LABEL}>
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                  }}
                >
                  {row.label}
                  {authType === row.value && (
                    <span style={BADGE_STYLE}>selected</span>
                  )}
                </span>
              </td>
              <td style={TD_VALUE}>
                <div style={{ marginBottom: 4 }}>{row.desc}</div>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {row.notes.map((n, ni) => (
                    <li
                      key={ni}
                      style={{
                        fontSize: 12,
                        color:
                          row.value === "TEST_ACCOUNT" &&
                          ni === row.notes.length - 1
                            ? "#c0392b"
                            : "#555",
                      }}
                    >
                      {n}
                    </li>
                  ))}
                </ul>
              </td>
            </tr>
            {i < rows.length - 1 && (
              <tr>
                <td colSpan={2} style={{ padding: "6px 0" }}>
                  <CPHrBreak />
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </CPModal>
);

export class UserRecord implements UserUpdateRequest {
  userId: number = -1;
  firstname: string = "";
  lastname: string = "";
  email: string = "";
  externalSystemId: string | null = null;
  payee: string | null = null;
  authType: string | null = null;
  password: string | null = null;
  roles: string[] = [];
  agencies: string[] = [];
  isActive: boolean = true;
}

interface UserDetailsModalProps {
  selectedUser: UserRecord | null;
  onClose: () => void;
  onSave: () => void;
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  selectedUser,
  onClose,
  onSave,
}) => {
  const { setToastMsg, setIsLoading } = useAppState();
  const {
    mutate,
    error,
    isLoading: userUpdateLoading,
  } = useUpsertUserMutation();
  const { data: roles, isLoading: isRolesLoading } = useRolesLookupQuery();
  const { data: agencies, isLoading: isAgenciesLoading } =
    useAgenciesLookupQuery();
  const { data: fullUser } = useAdminUserDetailQuery(selectedUser?.userId ?? -1);

  const [editingUser, setEditingUser] = React.useState<UserRecord | null>(
    selectedUser,
  );
  const [showSignonHelp, setShowSignonHelp] = React.useState(false);
  const [passwordDirty, setPasswordDirty] = React.useState(false);
  const [passwordFocused, setPasswordFocused] = React.useState(false);
  const [rolesBoxOptions, setRolesBoxOptions] = React.useState<CPBoxOption>({
    available: [],
    selected: [],
  });
  const [agencyBoxOptions, setAgencyBoxOptions] = React.useState<CPBoxOption>({
    available: [],
    selected: [],
  });

  React.useEffect(() => {
    if (!fullUser) return;
    setEditingUser((prev) =>
      prev ? { ...prev, externalSystemId: fullUser.externalSystemId, payee: fullUser.payee ?? null, authType: fullUser.authType } : prev,
    );
  }, [fullUser]);

  React.useEffect(() => {
    setIsLoading(userUpdateLoading);
  }, [userUpdateLoading]);

  React.useEffect(() => {
    if (!editingUser || isRolesLoading) return;
    setRolesBoxOptions({
      available: (roles ?? [])
        .filter((r) => !editingUser.roles.includes(r.name))
        .map((r) => ({ label: r.name, value: r.name, selected: false })),
      selected: editingUser.roles.map((r) => ({
        label: r,
        value: r,
        selected: false,
      })),
    });
  }, [isRolesLoading, roles]);

  React.useEffect(() => {
    if (!editingUser || isAgenciesLoading) return;
    setAgencyBoxOptions({
      available: (agencies ?? [])
        .filter((a) => !editingUser.agencies.includes(a.agency))
        .map((a) => ({
          label: `${a.agency} (${a.company})`,
          value: a.agency,
          selected: false,
        })),
      selected: editingUser.agencies.map((r) => ({
        label: r,
        value: r,
        selected: false,
      })),
    });
  }, [isAgenciesLoading]);

  const handleSave = (user: UserRecord) => {
    const userToSave: UserUpdateRequest = {
      userId: user.userId,
      firstname: user.firstname,
      lastname: user.lastname,
      email: user.email,
      externalSystemId: user.externalSystemId,
      payee: user.payee ?? "",
      authType: user.authType,
      password: user.authType === "TEST_ACCOUNT" && passwordDirty ? user.password : null,
      roles: rolesBoxOptions.selected.map((o) => o.value as string),
      agencies: agencyBoxOptions.selected.map((o) => o.value as string),
      isActive: user.isActive,
    };

    mutate(userToSave, {
      onSuccess: () => {
        setToastMsg("User saved successfully", "success");
        onSave();
      },
      onError: (error: unknown) => {
        if ((error as Record<string, unknown>)["errors"]) {
          setToastMsg(
            "Please check the form for errors and try again",
            "error",
          );
          return;
        }

        setToastMsg("An error occurred during the request.", "error");
      },
    });
  };

  return (
    <>
      <SignonHelpModal
        authType={editingUser?.authType ?? undefined}
        open={showSignonHelp}
        onClose={() => setShowSignonHelp(false)}
      />
      <CPActionsheetModal
        title="User Details"
        open={selectedUser !== null}
        onClose={onClose}
        position="right"
        width={550}
        actionBtns={
          <CPButton
            themeColor="primary"
            onClick={() => editingUser && handleSave(editingUser)}
          >
            Save
          </CPButton>
        }
      >
        {editingUser && (
          <Box
            spacing={24}
            flexDirection="column"
            style={{ paddingBottom: 16 }}
          >
            <Box spacing={12} flexDirection="column">
              <Box
                flexDirection="row"
                alignItems="flex-start"
                spacing={12}
                className="cp-actionsheet-input-box"
              >
                <CPTextBox
                  name="firstname"
                  label="Firstname"
                  value={editingUser.firstname}
                  required
                  error={error}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      firstname: e.target.value?.toString() || "",
                    })
                  }
                />
                <CPTextBox
                  name="lastname"
                  label="Lastname"
                  value={editingUser.lastname}
                  error={error}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      lastname: e.target.value?.toString() || "",
                    })
                  }
                />
                <div style={{ paddingTop: "1.5em" }}>
                  <CPCheckbox
                    name="isActive"
                    label="Active"
                    value={editingUser.isActive ?? true}
                    onChange={(e) =>
                      setEditingUser({ ...editingUser, isActive: e.value })
                    }
                  />
                </div>
              </Box>

              <Box className="cp-actionsheet-input-box">
                <CPEmailTextBox
                  name="email"
                  label="Email Address"
                  value={editingUser.email}
                  error={error}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      email: e.target.value?.toString() || "",
                    })
                  }
                  required
                />
              </Box>

              <Box
                flexDirection="row"
                alignItems="center"
                justifyContent="flex-start"
                spacing={6}
                style={{ padding: 0 }}
                className="cp-actionsheet-input-box"
              >
                <Box style={{ flex: 1, maxWidth: "90%" }}>
                  <CPDropDownList
                    name="authType"
                    label="Sign on option"
                    data={SIGNON_OPTIONS}
                    textField="text"
                    dataItemKey="value"
                    valueKey="value"
                    value={editingUser.authType ?? null}
                    onChange={(e) => {
                      setPasswordDirty(false);
                      setPasswordFocused(false);
                      setEditingUser({
                        ...editingUser,
                        authType: e.value as string,
                        password: null,
                      });
                    }}
                  />
                </Box>
                <CPHelpButton
                  onClick={() => setShowSignonHelp(true)}
                  title="Learn about sign on options"
                  aria-label="Sign on option help"
                />
              </Box>

              {editingUser.authType === "TEST_ACCOUNT" && (
                <Box className="cp-actionsheet-input-box">
                  <CPTextBox
                    name="password"
                    label="Account Password"
                    type="password"
                    value={
                      !passwordDirty && editingUser.userId !== -1 && !passwordFocused
                        ? "••••••••••••"
                        : (editingUser.password ?? "")
                    }
                    required
                    error={error}
                    onFocus={() => setPasswordFocused(true)}
                    onBlur={() => {
                      if (!passwordDirty) setPasswordFocused(false);
                    }}
                    onChange={(e) => {
                      setPasswordDirty(true);
                      setEditingUser({
                        ...editingUser,
                        password: e.target.value?.toString() || null,
                      });
                    }}
                    helpText="Set the shared password for this test account"
                  />
                </Box>
              )}
              <Box className="cp-actionsheet-input-box">
                <CPTextBox
                  name="externalSystemId"
                  label="External System Username / Mcleod Driver Code"
                  value={editingUser.externalSystemId ?? ""}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      externalSystemId: e.target.value?.toString() || null,
                    })
                  }
                  helpText="The main username used in other Evans systems (GreatEdge, EBH, etc)"
                />
              </Box>
              <Box className="cp-actionsheet-input-box">
                <CPTextBox
                  name="payee"
                  label="Payee (Optional)"
                  value={editingUser.payee ?? ""}
                  maxLength={20}
                  onChange={(e) =>
                    setEditingUser({
                      ...editingUser,
                      payee: e.target.value?.toString() || null,
                    })
                  }
                />
              </Box>
            </Box>

            <Box spacing={12} flexDirection="column">
              <Box className="cp-actionsheet-input-box">
                <CPListBox
                  headerText="Assigned Agencies"
                  boxOptions={agencyBoxOptions}
                  onChange={(e) => setAgencyBoxOptions(e)}
                  filterable={true}
                />
              </Box>

              <Box className="cp-actionsheet-input-box">
                <CPListBox
                  headerText="Assigned Roles"
                  boxOptions={rolesBoxOptions}
                  onChange={(e) => setRolesBoxOptions(e)}
                  filterable={true}
                />
              </Box>
            </Box>
          </Box>
        )}
      </CPActionsheetModal>
    </>
  );
};

export default UserDetailsModal;
